import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  getDoc,
  limit,
  startAfter,
  getCountFromServer,
  type QueryConstraint,
  type QueryDocumentSnapshot,
  type DocumentData,
} from "firebase/firestore";
import { useSearchParams } from "react-router-dom";
import { db } from "@/config/firebase";
import type { Product } from "@/types";
import ProductCard from "@/components/ProductCard";

const PAGE_SIZE = 24;

/**
 * ╔═══════════════════════════════════════════════════════════════════════╗
 * ║  ÍNDICES COMPOSTOS NECESSÁRIOS (criar no painel Firebase)           ║
 * ║                                                                      ║
 * ║  1. status ASC, minPrice ASC                                         ║
 * ║  2. status ASC, minPrice DESC                                        ║
 * ║  3. status ASC, nameLower ASC                                        ║
 * ║  4. status ASC, categories ASC (array-contains), minPrice ASC        ║
 * ║  5. status ASC, categories ASC (array-contains), minPrice DESC       ║
 * ║  6. status ASC, categories ASC (array-contains), nameLower ASC       ║
 * ║                                                                      ║
 * ║  Sem esses índices, as queries abaixo vão falhar com "failed to     ║
 * ║  build an index" no console do Firebase.                             ║
 * ╚═══════════════════════════════════════════════════════════════════════╝
 *
 * REGRAS DE QUERY:
 * - Busca por texto usa nameLower (range filter) → ordena por nameLower
 * - Filtro de preço usa minPrice (range filter) → ordena por minPrice
 * - Firestore permite APENAS UM range filter por query (em campos diferentes)
 * - Quando busca ativa: preço filtrado CLIENT-SIDE nos 24 resultados
 * - Quando busca inativa: preço filtrado SERVER-SIDE na query
 */

interface Filters {
  category: string | null;
  subcategory: string | null;
  search: string;
  priceRange: string | null;
  sort: string;
}

const PRICE_RANGES = [
  { id: "0-2", label: "Até R$2", min: 0, max: 2 },
  { id: "2-5", label: "R$2 a R$5", min: 2, max: 5 },
  { id: "5-10", label: "R$5 a R$10", min: 5, max: 10 },
  { id: "10-20", label: "R$10 a R$20", min: 10, max: 20 },
  { id: "20+", label: "Acima de R$20", min: 20, max: Infinity },
];

export default function CatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [allProductsRaw, setAllProductsRaw] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const lastDocRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [filters, setFilters] = useState<Filters>({
    category: searchParams.get("cat"),
    subcategory: searchParams.get("sub"),
    search: "",
    priceRange: null,
    sort: "",
  });
  const [materials, setMaterials] = useState<Set<string>>(new Set());
  const [savedCategories, setSavedCategories] = useState<string[] | null>(null);
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
  const [totalProducts, setTotalProducts] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const activeCatRef = useRef<HTMLButtonElement>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleProductStatusChange = useCallback((productId: string, newStatus: string) => {
    if (newStatus === "inactive") {
      setAllProductsRaw((prev) => prev.filter((p) => p.id !== productId));
    }
  }, []);
  const sidebarActiveRef = useRef<HTMLButtonElement>(null);

  const normalize = (s: string) => {
    try {
      return (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    } catch {
      return (s || "").toLowerCase();
    }
  };

  // Restaurar posição de scroll ao voltar do product detail
  useEffect(() => {
    const saved = sessionStorage.getItem("catalog_scroll_position");
    if (saved) {
      setTimeout(() => {
        window.scrollTo(0, parseInt(saved, 10));
        sessionStorage.removeItem("catalog_scroll_position");
      }, 50);
    }
  }, []);

  // ── OTIMIZAÇÃO: 3 leituras (categories + materials + totalProducts) para o sidebar ──
  useEffect(() => {
    Promise.all([
      getDoc(doc(db, "admin_config", "materials")),
      getDoc(doc(db, "admin_config", "categories")),
      getCountFromServer(query(collection(db, "products"), where("status", "==", "active"))),
    ]).then(([matSnap, catSnap, countSnap]) => {
      if (matSnap.exists()) {
        const names = (matSnap.data()?.names ?? []) as string[];
        setMaterials(new Set(names.map((n) => n.toLowerCase())));
      }
      if (catSnap.exists()) {
        const data = catSnap.data();
        const names = (data?.names ?? []) as string[];
        setSavedCategories(names);
        setCategoryCounts((data?.counts ?? {}) as Record<string, number>);
      }
      setTotalProducts(countSnap.data().count);
    }).catch(() => {});
  }, []);

  // ══════════════════════════════════════════════════════════════════
  // FUNÇÃO DE CARREGAMENTO — query server-side com filtros
  // ══════════════════════════════════════════════════════════════════
  //
  // Lógica de query:
  //   BUSCA ativa → where nameLower >= term && <= term+\uf8ff → orderBy nameLower
  //   Sem busca   → [where minPrice >= min && <= max] → orderBy minPrice
  //   Em ambos    → where categories array-contains (se selecionada)
  //
  // IMPORTANTE: Firestore permite no máximo 1 range filter por campo diferente.
  // nameLower (busca) e minPrice (preço) são range filters em campos diferentes,
  // por isso NÃO podem coexistir na mesma query server-side.
  // ══════════════════════════════════════════════════════════════════
  const loadProducts = useCallback(
    async (reset: boolean, currentFilters: Filters) => {
      if (reset) {
        setLoading(true);
        setAllProductsRaw([]);
        lastDocRef.current = null;
        setHasMore(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const constraints: QueryConstraint[] = [
          where("status", "==", "active"),
        ];

        const searchTerm = normalize(currentFilters.search.trim());
        const hasSearch = searchTerm.length >= 2;

        // ── Filtro por categoria (array-contains — equality, sempre permitido) ──
        if (currentFilters.category) {
          constraints.push(
            where("categories", "array-contains", currentFilters.category)
          );
        }

        if (hasSearch) {
          // ── BUSCA NO FIRESTORE (Prefix em nameLower OU busca por código SKU) ──
          const cleanSearchTerm = searchTerm.replace(/[²³×*()]/g, "").trim();
          const firstToken = cleanSearchTerm.split(/\s+/)[0] || cleanSearchTerm;

          // Se o termo for um SKU (ex: ST83-2051 ou ST83), tenta também buscar por prefixo do primeiro token
          constraints.push(where("nameLower", ">=", firstToken));
          constraints.push(where("nameLower", "<=", firstToken + "\uf8ff"));
          constraints.push(orderBy("nameLower", "asc"));
        } else {
          // ── FILTRO DE PREÇO SERVER-SIDE ──
          const priceRange = currentFilters.priceRange
            ? PRICE_RANGES.find((r) => r.id === currentFilters.priceRange)
            : null;

          if (priceRange) {
            // Range filter: minPrice >= min && minPrice <= max
            // ⚠️ ÍNDICE: (status, [categories], minPrice ASC|DESC)
            constraints.push(where("minPrice", ">=", priceRange.min));
            if (priceRange.max !== Infinity) {
              constraints.push(where("minPrice", "<=", priceRange.max));
            }
          }

          // ── ORDENAÇÃO POR PREÇO ──
          const sortDir = currentFilters.sort === "price-desc" ? "desc" : "asc";
          constraints.push(orderBy("minPrice", sortDir));
        }

        // ── PAGINAÇÃO ──
        constraints.push(limit(PAGE_SIZE));
        if (!reset && lastDocRef.current) {
          constraints.push(startAfter(lastDocRef.current));
        }

        const q = query(collection(db, "products"), ...constraints);
        let snapshot = await getDocs(q);

        // Fallback: se a busca por nameLower retornar vazia e for termo de SKU, busca por customSku/supplierRef
        if (snapshot.empty && hasSearch) {
          const skuQuery = query(
            collection(db, "products"),
            where("status", "==", "active"),
            where("customSku", "==", currentFilters.search.trim())
          );
          const skuSnap = await getDocs(skuQuery);
          if (!skuSnap.empty) {
            snapshot = skuSnap;
          } else {
            const refQuery = query(
              collection(db, "products"),
              where("status", "==", "active"),
              where("supplierRef", "==", currentFilters.search.trim())
            );
            const refSnap = await getDocs(refQuery);
            if (!refSnap.empty) snapshot = refSnap;
          }
        }

        let items = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() } as Product))
          .filter((p) => p.imageUrl && p.imageUrl.trim().length > 0);

        // ── FILTRO DE PREÇO CLIENT-SIDE (quando busca ativa) ──
        // Firestore não permite 2 range filters em campos diferentes.
        // Quando search ativo, preço é filtrado aqui nos 24 resultados.
        if (hasSearch && currentFilters.priceRange) {
          const priceRange = PRICE_RANGES.find(
            (r) => r.id === currentFilters.priceRange
          );
          if (priceRange) {
            items = items.filter((p) => {
              const price = getApproxPrice(p);
              return price >= priceRange.min && price < priceRange.max;
            });
          }
        }

        if (reset) {
          setAllProductsRaw(items);
        } else {
          setAllProductsRaw((prev) => [...prev, ...items]);
        }

        const lastVisible =
          snapshot.docs.length > 0
            ? snapshot.docs[snapshot.docs.length - 1]
            : null;
        lastDocRef.current = lastVisible ?? null;
        setHasMore(snapshot.docs.length >= PAGE_SIZE);
      } catch (error) {
        console.error("Erro ao buscar produtos:", error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Preço aproximado do campo minPrice (para filtro client-side quando search ativo)
  function getApproxPrice(product: Product): number {
    if (typeof product.minPrice === "number") return product.minPrice;
    const cust =
      product.customizationCost.silk_screen ??
      product.customizationCost.uv_print ??
      product.customizationCost.laser ??
      0;
    const sup = product.variants[0]?.price ?? 0;
    return sup + cust;
  }

  // ══════════════════════════════════════════════════════════════════
  // CARGA INICIAL — reset query quando categoria muda
  // ══════════════════════════════════════════════════════════════════
  useEffect(() => {
    let cancelled = false;
    if (!cancelled) {
      loadProducts(true, filters);
    }
    return () => {
      cancelled = true;
    };
  }, [filters.category, filters.search, filters.priceRange, filters.sort, loadProducts]);

  // ── CATEGORIAS ──
  const allCategories = useMemo(() => {
    if (savedCategories && savedCategories.length > 0) {
      return savedCategories.sort((a, b) => a.localeCompare(b, "pt-BR"));
    }
    const cats = new Set<string>();
    for (const p of allProductsRaw) {
      const raw = p.categories;
      let arr: string[] = [];
      if (Array.isArray(raw)) arr = raw;
      else if (raw && typeof raw === "object")
        arr = Object.values(raw).filter(
          (v): v is string => typeof v === "string"
        );
      for (const c of arr) {
        if (c && c.length > 0 && !materials.has(c.toLowerCase())) cats.add(c);
      }
    }
    return Array.from(cats).sort();
  }, [savedCategories, allProductsRaw, materials]);

  const categoryCountsMemo = useMemo(() => {
    if (Object.keys(categoryCounts).length > 0) return categoryCounts;
    const rawCounts: Record<string, number> = {};
    for (const p of allProductsRaw) {
      const raw = p.categories;
      let arr: string[] = [];
      if (Array.isArray(raw)) arr = raw;
      else if (raw && typeof raw === "object")
        arr = Object.values(raw).filter(
          (v): v is string => typeof v === "string"
        );
      for (const c of arr) {
        const key = normalize(c);
        rawCounts[key] = (rawCounts[key] ?? 0) + 1;
      }
    }
    const result: Record<string, number> = {};
    for (const cat of allCategories) {
      result[cat] = rawCounts[normalize(cat)] ?? 0;
    }
    for (const [key, count] of Object.entries(rawCounts)) {
      if (!(key in result)) result[key] = count;
    }
    return result;
  }, [categoryCounts, allProductsRaw, allCategories]);

  // ── SUBCATEGORIAS (client-side — materials overlap) ──
  const subcategories = useMemo(() => {
    if (!filters.category || materials.size === 0) return [];
    const subs = new Set<string>();
    for (const p of allProductsRaw) {
      const raw = p.categories;
      let cats: string[] = [];
      if (Array.isArray(raw)) cats = raw;
      else if (raw && typeof raw === "object")
        cats = Object.values(raw).filter(
          (v): v is string => typeof v === "string"
        );
      if (cats.includes(filters.category)) {
        for (const c of cats) {
          if (materials.has(c.toLowerCase()) && c !== filters.category)
            subs.add(c);
        }
      }
    }
    return Array.from(subs).sort();
  }, [allProductsRaw, filters.category, materials]);

  // Produtos filtrados por subcategory (client-side)
  const displayProducts = useMemo(() => {
    if (!filters.subcategory) return allProductsRaw;
    return allProductsRaw.filter((p) => {
      const raw = p.categories;
      let cats: string[] = [];
      if (Array.isArray(raw)) cats = raw;
      else if (raw && typeof raw === "object")
        cats = Object.values(raw).filter(
          (v): v is string => typeof v === "string"
        );
      return cats.includes(filters.subcategory!);
    });
  }, [allProductsRaw, filters.subcategory]);

  const categorySuggestions = (() => {
    if (filters.search.length < 2) return [];
    try {
      const terms = filters.search
        .split(/\s+/)
        .filter(Boolean)
        .map(normalize);
      return allCategories
        .filter((cat) => {
          const catNorm = normalize(cat);
          return terms.every((t) => catNorm.includes(t));
        })
        .map((cat) => ({ name: cat, id: cat }));
    } catch {
      return [];
    }
  })();

  // Auto-scroll para a categoria ativa
  useEffect(() => {
    if (!filters.category) return;
    const timer = setTimeout(() => {
      activeCatRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
      sidebarActiveRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }, 100);
    return () => clearTimeout(timer);
  }, [filters.category, allCategories]);

  // ── HANDLERS: cada mudança dispara nova query server-side com reset ──

  const handleSearch = useCallback((search: string) => {
    // Debounce: espera 300ms após última tecla para evitar queries excessivas
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!search || search.length < 2) {
      setFilters((prev) => ({ ...prev, search }));
      return;
    }
    searchTimeoutRef.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search }));
    }, 300);
  }, []);

  const handleSearchImmediate = useCallback((search: string) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    setFilters((prev) => ({ ...prev, search }));
  }, []);

  const handleCategoryChange = useCallback(
    (category: string | null) => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      setFilters((prev) => ({
        ...prev,
        category,
        subcategory: null,
        search: "",
        priceRange: null,
      }));
      if (category) {
        setSearchParams({ cat: category });
      } else {
        setSearchParams({});
      }
    },
    [setSearchParams]
  );

  const handleSubcategoryChange = useCallback((subcategory: string | null) => {
    setFilters((prev) => ({ ...prev, subcategory }));
  }, []);

  const handlePriceRangeChange = useCallback((priceRange: string | null) => {
    setFilters((prev) => ({ ...prev, priceRange }));
  }, []);

  const handleSortChange = useCallback((sort: string) => {
    setFilters((prev) => ({ ...prev, sort }));
  }, []);

  const hasActiveSearch = filters.search.trim().length >= 2;

  return (
    <div className="px-4 py-6 sm:px-6 sm:py-10">
      <div className="mx-auto max-w-site">
        <h1 className="mb-6 text-2xl font-bold text-text-primary sm:mb-8 sm:text-3xl">
          Catálogo de Produtos
        </h1>

        {/* Busca — mobile */}
        <div className="relative mb-4 sm:mb-6 sm:hidden">
          <div className="rounded-xl border border-border bg-surface p-3">
            <div className="flex items-center gap-3">
              <svg
                className="h-5 w-5 shrink-0 text-text-muted"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                />
              </svg>
              <input
                ref={searchRef}
                type="text"
                placeholder="Buscar produto..."
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() =>
                  setTimeout(() => setShowSuggestions(false), 200)
                }
                className="w-full bg-transparent text-sm text-text-primary placeholder-text-muted outline-none"
              />
              {filters.search && (
                <button
                  onClick={() => {
                    handleSearchImmediate("");
                    setShowSuggestions(false);
                  }}
                  className="shrink-0 rounded-full p-1 text-text-muted hover:text-text-primary"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
          {showSuggestions && categorySuggestions.length > 0 && (
            <div className="absolute left-0 right-0 z-50 mt-1 rounded-xl border border-border bg-bg shadow-lg">
              {categorySuggestions.map((s) => (
                <button
                  key={s.id}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleCategoryChange(s.id);
                    setShowSuggestions(false);
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm hover:bg-surface"
                >
                  <span className="capitalize text-text-primary">
                    {s.name}
                  </span>
                  {categoryCountsMemo[s.name] != null && (
                    <span className="ml-auto text-[11px] text-text-muted">
                      {categoryCountsMemo[s.name]}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Categorias mobile — scroll horizontal */}
        <div className="mb-4 sm:hidden">
          <div className="-mx-4 flex gap-1.5 overflow-x-auto px-4 pb-2">
            <button
              onClick={() => handleCategoryChange(null)}
              ref={filters.category === null ? activeCatRef : undefined}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                filters.category === null
                  ? "bg-accent text-white"
                  : "border border-border text-text-secondary"
              }`}
            >
              Todos
            </button>
            {allCategories
              .filter(
                (cat) =>
                  (categoryCountsMemo[cat] ?? 0) > 0 ||
                  filters.category === cat
              )
              .map((cat) => (
                <button
                  key={cat}
                  ref={filters.category === cat ? activeCatRef : undefined}
                  onClick={() => handleCategoryChange(cat)}
                  className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                    filters.category === cat
                      ? "bg-accent text-white"
                      : "border border-border text-text-secondary"
                  }`}
                >
                  {cat}
                </button>
              ))}
          </div>
        </div>

        {/* Subcategorias (materiais) — todos os breakpoints */}
        {filters.category && subcategories.length > 0 && (
          <div className="mb-4">
            <div className="-mx-4 flex gap-1.5 overflow-x-auto px-4 pb-2 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0">
              <button
                onClick={() => handleSubcategoryChange(null)}
                className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors sm:shrink ${
                  filters.subcategory === null
                    ? "border-accent bg-accent/10 text-accent"
                    : "border-border border-dashed text-text-muted"
                }`}
              >
                Todos
              </button>
              {subcategories.map((sub) => (
                <button
                  key={sub}
                  onClick={() => handleSubcategoryChange(sub)}
                  className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-medium capitalize transition-colors sm:shrink ${
                    filters.subcategory === sub
                      ? "border-accent bg-accent/10 text-accent"
                      : "border-border border-dashed text-text-muted"
                  }`}
                >
                  {sub}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-8">
          {/* Sidebar filtros — desktop */}
          <aside className="hidden w-52 shrink-0 sm:block">
            <div className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto overscroll-contain pb-4">
              {/* Busca */}
              <div className="relative mb-5">
                <input
                  id="search-desktop"
                  type="text"
                  placeholder="Buscar produto..."
                  value={filters.search}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() =>
                    setTimeout(() => setShowSuggestions(false), 200)
                  }
                  className="w-full rounded-lg border border-border bg-bg px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                />
                {showSuggestions && categorySuggestions.length > 0 && (
                  <div className="absolute left-0 right-0 z-50 mt-1 rounded-lg border border-border bg-bg shadow-lg">
                    {categorySuggestions.map((s) => (
                      <button
                        key={s.id}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleCategoryChange(s.id);
                          setShowSuggestions(false);
                        }}
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-surface"
                      >
                        <span className="capitalize text-text-primary">
                          {s.name}
                        </span>
                        {categoryCountsMemo[s.name] != null && (
                          <span className="text-[11px] text-text-muted">
                            {categoryCountsMemo[s.name]}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Categorias */}
              <div className="space-y-0.5">
                <button
                  ref={
                    filters.category === null ? sidebarActiveRef : undefined
                  }
                  onClick={() => handleCategoryChange(null)}
                  className={`flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-left text-[13px] transition-colors ${
                    filters.category === null
                      ? "bg-accent/10 font-medium text-accent"
                      : "text-text-secondary hover:bg-surface hover:text-text-primary"
                  }`}
                >
                  <span>Todos</span>
                  <span className="text-[11px] tabular-nums text-text-muted">
                    {totalProducts}
                  </span>
                </button>
                {allCategories
                  .filter(
                    (category) =>
                      (categoryCountsMemo[category] ?? 0) > 0 ||
                      filters.category === category
                  )
                  .map((category) => (
                    <button
                      key={category}
                      ref={
                        filters.category === category
                          ? sidebarActiveRef
                          : undefined
                      }
                      onClick={() => handleCategoryChange(category)}
                      className={`flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-left text-[13px] capitalize transition-colors ${
                        filters.category === category
                          ? "bg-accent/10 font-medium text-accent"
                          : "text-text-secondary hover:bg-surface hover:text-text-primary"
                      }`}
                    >
                      <span className="truncate">{category}</span>
                      <span className="ml-2 shrink-0 text-[11px] tabular-nums text-text-muted">
                        {categoryCountsMemo[category] ?? 0}
                      </span>
                    </button>
                  ))}
              </div>
            </div>
          </aside>

          {/* Grid de produtos */}
          <main className="flex-1">
            {/* Banner da categoria ativa */}
            {filters.category && (
              <div className="mb-4 flex items-center justify-between rounded-lg border border-accent/20 bg-accent/5 px-4 py-3">
                <div className="flex items-center gap-2">
                  <svg
                    className="h-4 w-4 text-accent"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25z"
                    />
                  </svg>
                  <span className="text-sm font-semibold capitalize text-accent">
                    {filters.category}
                  </span>
                </div>
                <button
                  onClick={() => handleCategoryChange(null)}
                  className="text-xs text-text-muted hover:text-text-primary"
                >
                  Limpar
                </button>
              </div>
            )}

            {/* Filtros extras */}
            <div className="mb-4">
              {/* Faixa de preço — desativada quando busca ativa (Firestore limitação) */}
              <div className="mb-3">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-text-muted">
                  Faixa de Preço
                  {hasActiveSearch && (
                    <span className="ml-2 text-accent normal-case">
                      (indisponível durante busca)
                    </span>
                  )}
                </p>
                <div className="flex gap-1.5 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible">
                  <button
                    onClick={() => handlePriceRangeChange(null)}
                    disabled={hasActiveSearch}
                    className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition-colors sm:shrink disabled:cursor-not-allowed disabled:opacity-40 ${
                      filters.priceRange === null && !hasActiveSearch
                        ? "bg-accent text-white"
                        : "border border-border text-text-secondary hover:border-border-hover"
                    }`}
                  >
                    Todos
                  </button>
                  {PRICE_RANGES.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => handlePriceRangeChange(r.id)}
                      disabled={hasActiveSearch}
                      className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition-colors sm:shrink disabled:cursor-not-allowed disabled:opacity-40 ${
                        filters.priceRange === r.id && !hasActiveSearch
                          ? "bg-accent text-white"
                          : "border border-border text-text-secondary hover:border-border-hover"
                      }`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Ordenar por — quando busca ativa, ordena por nome (server) */}
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-medium uppercase tracking-wide text-text-muted">
                  Ordenar por
                  {hasActiveSearch && (
                    <span className="ml-2 text-accent normal-case">
                      (por nome)
                    </span>
                  )}
                </p>
                <select
                  value={hasActiveSearch ? "name" : filters.sort}
                  onChange={(e) => handleSortChange(e.target.value)}
                  disabled={hasActiveSearch}
                  className="rounded-lg border border-border bg-bg px-3 py-2 text-xs font-medium text-text-secondary outline-none focus:border-accent disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <option value="default">Padrão</option>
                  <option value="price-asc">Menor preço</option>
                  <option value="price-desc">Maior preço</option>
                  {hasActiveSearch && <option value="name">Nome (A-Z)</option>}
                </select>
              </div>

              {/* Limpar filtros */}
              {(filters.priceRange || filters.sort !== "default") &&
                !hasActiveSearch && (
                  <button
                    onClick={() => {
                      handleSortChange("default");
                      handlePriceRangeChange(null);
                    }}
                    className="mt-2 text-xs text-accent hover:underline"
                  >
                    Limpar filtros
                  </button>
                )}
            </div>

            {loading ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-64 animate-pulse rounded-lg border border-border bg-surface sm:h-80"
                  />
                ))}
              </div>
            ) : displayProducts.length === 0 ? (
              <div className="py-20 text-center">
                <p className="text-lg text-text-muted">
                  Nenhum produto encontrado.
                </p>
                {(filters.priceRange || filters.search) && (
                  <button
                    onClick={() => {
                      handleSearchImmediate("");
                      handlePriceRangeChange(null);
                    }}
                    className="mt-3 text-sm text-accent hover:underline"
                  >
                    Limpar filtros
                  </button>
                )}
              </div>
            ) : (
              <>
                <p className="mb-3 text-sm text-text-muted">
                  {displayProducts.length} produto
                  {displayProducts.length !== 1 ? "s" : ""} exibido
                  {displayProducts.length !== 1 ? "s" : ""}
                  {hasMore && (
                    <span className="ml-2 text-accent">
                      (mais disponíveis)
                    </span>
                  )}
                </p>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
                  {displayProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onStatusChange={handleProductStatusChange}
                    />
                  ))}
                </div>

                {hasMore && (
                  <div className="mt-8 flex justify-center">
                    <button
                      onClick={() => loadProducts(false, filters)}
                      disabled={loadingMore}
                      className="rounded-lg border border-border bg-bg px-8 py-3 text-sm font-medium text-text-secondary transition-colors hover:border-border-hover hover:text-text-primary disabled:opacity-50"
                    >
                      {loadingMore ? (
                        <span className="flex items-center gap-2">
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-accent border-t-transparent" />
                          Carregando...
                        </span>
                      ) : (
                        "Carregar mais produtos"
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
